/*
 * UNIK wishlist page (F4-06, F6-03; sections/unik-wishlist.liquid).
 *
 * Shows the favourites from window.UnikWishlist (assets/unik-wishlist.js), newest first as in
 * the Shella reference, as the store's product cards. Each card is the Liquid card from
 * sections/unik-wishlist-card.liquid, fetched with the Section Rendering API on the product
 * URL with the saved variant (10 s timeout; a failed product never hides the others). Cards
 * are cached per page view, so removing a favourite re-renders without new requests. While
 * they load, placeholders of the card's size keep the page from jumping (tiltak 1.5).
 *
 * A count of the shown products sits above the grid, with "Fjern alle" when there is more
 * than one. The heart on a card removes that favourite; there is no add to cart here.
 */
(() => {
  if (customElements.get('unik-wishlist-page')) return;

  const TIMEOUT = 10000;
  const SECTION = 'unik-wishlist-card';

  function fetchCard({ handle, variantId }) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => controller?.abort(), TIMEOUT);
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(variantId)}&section_id=${SECTION}`;
    return fetch(url, { signal: controller?.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.unik-product-card');
        if (!card) throw new Error('No card');
        return card.closest('product-component')?.outerHTML || card.outerHTML;
      })
      .finally(() => clearTimeout(timer));
  }

  class UnikWishlistPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cards = new Map();
      this.request = 0;
      this.list = this.querySelector('[data-unik-wishlist-list]');
      this.querySelector('[data-unik-wishlist-clear]')?.addEventListener('click', () => window.UnikWishlist?.clear());
      this.onChange = () => this.load();
      document.addEventListener(window.UnikWishlist?.EVENT || 'unik:wishlist-change', this.onChange);
      this.reserve();
      this.load();
    }

    /** Until the cards are in, one placeholder per saved favourite keeps the grid's height (CLS). */
    reserve() {
      const count = (window.UnikWishlist?.items() || []).length;
      this.list.replaceChildren(
        ...Array.from({ length: count }, () => {
          const cell = document.createElement('li');
          cell.className = 'grid__item unik-wishlist-page__placeholder';
          cell.setAttribute('aria-hidden', 'true');
          return cell;
        })
      );
    }

    disconnectedCallback() {
      document.removeEventListener(window.UnikWishlist?.EVENT || 'unik:wishlist-change', this.onChange);
    }

    async load() {
      const request = ++this.request;
      const saved = (window.UnikWishlist?.items() || []).slice().reverse();
      const missing = saved.filter((item) => !this.cards.has(String(item.variantId)));
      const results = await Promise.allSettled(missing.map((item) => fetchCard(item)));
      missing.forEach((item, index) => {
        if (results[index].status === 'fulfilled') this.cards.set(String(item.variantId), results[index].value);
      });
      if (request !== this.request) return; // a newer change started a newer load
      const shown = saved.filter((item) => this.cards.has(String(item.variantId)));
      this.render(shown);
      const status = this.querySelector('[data-unik-wishlist-status]');
      if (status) status.textContent = results.some((result) => result.status === 'rejected') ? this.dataset.messageLoadError || '' : '';
    }

    render(shown) {
      const count = shown.length;
      this.querySelector('[data-unik-wishlist-loading]')?.setAttribute('hidden', '');
      this.querySelector('[data-unik-wishlist-empty]').hidden = count > 0;
      this.querySelector('[data-unik-wishlist-head]').hidden = count === 0;
      this.querySelector('[data-unik-wishlist-clear]').hidden = count <= 1;
      const label = count === 1 ? this.dataset.labelCountOne : this.dataset.labelCountOther;
      this.querySelector('[data-unik-wishlist-count]').textContent = (label || '').replace('__count__', String(count));
      this.list.replaceChildren(
        ...shown.map((item) => {
          const cell = document.createElement('li');
          cell.className = 'grid__item';
          cell.dataset.variantId = String(item.variantId);
          cell.innerHTML = this.cards.get(String(item.variantId));
          return cell;
        })
      );
    }
  }

  customElements.define('unik-wishlist-page', UnikWishlistPage);
})();
